/**
 * Upload validation by content inspection.
 *
 * The rule this module exists to enforce: **the client's `Content-Type` is
 * never trusted.** It is an attacker-controlled string. A file named
 * `logo.png` with `Content-Type: image/png` can contain an HTML document, a
 * ZIP archive or a Windows executable, and if the server stores the declared
 * type and later serves the bytes back with it, the server has become a
 * content-hosting service for whatever the attacker wanted to distribute. The
 * only reliable signal is the file's own leading bytes.
 *
 * So the flow is: read the first 32 bytes, match them against a fixed table of
 * signatures, and *derive* both the stored content type and the extension from
 * what was found. The declared type is used for nothing except logging the
 * mismatch, which is useful signal when triaging an upload that turned out to
 * be something else.
 *
 * ## Why SVG is not on the list
 *
 * SVG is a legitimate image format and is deliberately excluded. An SVG is an
 * XML document that can contain `<script>`, `<foreignObject>` and external
 * entity references. Served from the same origin as the app with an
 * `image/svg+xml` content type, it executes as script in the app's origin and
 * can read the refresh cookie path. Mitigating that properly requires either
 * serving every attachment from a separate origin or running a sanitiser over
 * the XML - both are real work, and neither is worth it for a project board.
 * The honest trade-off is stated in docs/SECURITY.md rather than papered over
 * with a `Content-Disposition: attachment` header, which stops navigation but
 * not an `<img>` or `<object>` embed.
 *
 * ## Why the text branch is a heuristic and is last
 *
 * There is no magic number for "plain text". The check is therefore negative:
 * reject anything containing a NUL byte or a known binary signature, then
 * require the sample to decode as strict UTF-8. That ordering matters - a
 * naive "is it printable ASCII" test accepts a ZIP archive, because `PK\x03\x04`
 * begins with two perfectly printable letters. The binary signatures are
 * therefore tested first, and the text heuristic only runs on what survives.
 */

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/**
 * Ceiling for the inline asset driver. D1 is a SQLite database read over the
 * same single-threaded HTTP path as every board query, so a BLOB competes
 * directly with realtime traffic for the same connection. 512 KB keeps a row
 * comfortably under the point where a single read dominates a request's
 * latency. See docs/DECISIONS.md ADR-007 for why this exists at all.
 */
export const INLINE_MAX_BYTES = 512 * 1024;

export type MediaCategory = 'image' | 'document' | 'text';

export interface SniffedMedia {
  contentType: string;
  extension: string;
  category: MediaCategory;
}

export type SniffResult =
  | { ok: true; media: SniffedMedia }
  | { ok: false; reason: 'empty' | 'too_short' | 'unsupported_type' | 'binary_disguised_as_text' | 'not_utf8' };

/** How many leading bytes are needed. 32 covers the longest signature here
 * (AVIF's `ftyp` box header plus a 4-byte brand plus room to spare) without
 * buffering more of a potentially large file than necessary. */
const SAMPLE_BYTES = 32;

function startsWith(bytes: Uint8Array, signature: readonly number[], offset = 0): boolean {
  if (bytes.length < offset + signature.length) return false;
  for (let index = 0; index < signature.length; index += 1) {
    if (bytes[offset + index] !== signature[index]) return false;
  }
  return true;
}

function asciiAt(bytes: Uint8Array, offset: number, text: string): boolean {
  if (bytes.length < offset + text.length) return false;
  for (let index = 0; index < text.length; index += 1) {
    if (bytes[offset + index] !== text.charCodeAt(index)) return false;
  }
  return true;
}

/** Formats that begin with printable bytes and would otherwise slip past the
 * text heuristic. Each entry is a reason to reject, not a type to accept. */
const BINARY_LOOKALIKES: readonly { signature: readonly number[]; label: string }[] = [
  { signature: [0x50, 0x4b, 0x03, 0x04], label: 'zip' }, // PK\x03\x04 - zip, docx, xlsx, jar
  { signature: [0x50, 0x4b, 0x05, 0x06], label: 'zip-empty' },
  { signature: [0x1f, 0x8b], label: 'gzip' },
  { signature: [0x7f, 0x45, 0x4c, 0x46], label: 'elf' },
  { signature: [0xd0, 0xcf, 0x11, 0xe0], label: 'ole2' }, // legacy .doc/.xls
  { signature: [0x42, 0x4d], label: 'bmp' },
  { signature: [0x25, 0x21, 0x50, 0x53], label: 'postscript' },
  { signature: [0x52, 0x61, 0x72, 0x21], label: 'rar' },
  { signature: [0x37, 0x7a, 0xbc, 0xaf], label: '7z' },
];

/**
 * Identify a file from its leading bytes.
 *
 * The caller must supply at least `SAMPLE_BYTES` when available; a file shorter
 * than that is not automatically rejected (a 4-byte text file is legitimate),
 * but the signature checks that need more bytes simply fail to match.
 */
export function sniffContentType(bytes: Uint8Array): SniffResult {
  if (bytes.length === 0) return { ok: false, reason: 'empty' };

  const sample = bytes.subarray(0, Math.min(bytes.length, SAMPLE_BYTES));

  // --- Images ---------------------------------------------------------------
  // JPEG. Only the 3-byte SOI + marker prefix is checked. A full JPEG has
  // dozens of valid marker sequences, so parsing further buys accuracy the
  // server does not need: it is not decoding the image, only deciding whether
  // it is safe to serve back with an image content type.
  if (startsWith(sample, [0xff, 0xd8, 0xff])) {
    return { ok: true, media: { contentType: 'image/jpeg', extension: 'jpg', category: 'image' } };
  }

  // PNG. The full 8-byte signature includes CRLF and a DOS EOF marker
  // specifically so that a text-mode FTP transfer corrupts it detectably.
  if (startsWith(sample, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return { ok: true, media: { contentType: 'image/png', extension: 'png', category: 'image' } };
  }

  // GIF87a / GIF89a. Both versions are accepted; 89a is a superset.
  if (asciiAt(sample, 0, 'GIF87a') || asciiAt(sample, 0, 'GIF89a')) {
    return { ok: true, media: { contentType: 'image/gif', extension: 'gif', category: 'image' } };
  }

  // WebP is a RIFF container. Checking `RIFF` alone would also match WAV and
  // AVI, so the four-byte form type at offset 8 is required as well.
  if (asciiAt(sample, 0, 'RIFF') && asciiAt(sample, 8, 'WEBP')) {
    return { ok: true, media: { contentType: 'image/webp', extension: 'webp', category: 'image' } };
  }

  // AVIF is an ISO-BMFF box: a 4-byte size, then `ftyp`, then the brand.
  // `avis` is the animated variant and shares the same content type.
  if (asciiAt(sample, 4, 'ftyp') && (asciiAt(sample, 8, 'avif') || asciiAt(sample, 8, 'avis'))) {
    return { ok: true, media: { contentType: 'image/avif', extension: 'avif', category: 'image' } };
  }

  // --- Documents ------------------------------------------------------------
  // PDF. The header is required by the spec to be within the first 1024 bytes
  // rather than exactly at offset 0, because some generators emit a BOM or a
  // comment first. Only offset 0 is checked here: a PDF that does not start
  // with `%PDF-` is unusual enough that rejecting it is the safer default, and
  // a byte-scan for the marker elsewhere would let an attacker hide it inside
  // another format.
  if (asciiAt(sample, 0, '%PDF-')) {
    return { ok: true, media: { contentType: 'application/pdf', extension: 'pdf', category: 'document' } };
  }

  // --- Binary that looks like text -----------------------------------------
  for (const lookalike of BINARY_LOOKALIKES) {
    if (startsWith(sample, lookalike.signature)) {
      return { ok: false, reason: 'unsupported_type' };
    }
  }

  // --- Plain text -----------------------------------------------------------
  // A NUL byte in the first 32 bytes is the single most reliable "this is not
  // text" signal; no text encoding in use produces one at the start of a file.
  for (const byte of sample) {
    if (byte === 0x00) return { ok: false, reason: 'binary_disguised_as_text' };
  }

  if (!decodesAsUtf8(sample)) return { ok: false, reason: 'not_utf8' };

  return { ok: true, media: { contentType: 'text/plain', extension: 'txt', category: 'text' } };
}

/**
 * Strict UTF-8 decode of a byte sample, tolerant of a sample that ends mid-character.
 *
 * `fatal: true` is what distinguishes a genuine text file from a binary blob whose
 * leading bytes happen to be ASCII - substituting U+FFFD would accept both.
 *
 * The subtlety this function exists for: the sample is a fixed 32-byte window, and
 * a UTF-8 character is one to four bytes. A file of Japanese text will almost
 * always have its 32nd byte in the middle of a three-byte character, so a naive
 * strict decode rejects perfectly valid text. Retrying on prefixes shortened by one
 * to three bytes finds the character boundary. If none of the four decodes
 * succeeds, the bytes are genuinely not UTF-8.
 *
 * The alternative - decode the whole file - would mean buffering an entire upload
 * to decide whether to accept it, which is the thing the fixed sample exists to
 * avoid.
 */
function decodesAsUtf8(sample: Uint8Array): boolean {
  // `ignoreBOM: false` is stated explicitly rather than omitted: the Workers
  // runtime types declare it as required and the browser types declare it
  // optional, so naming it satisfies both without a cast.
  const options = { fatal: true, ignoreBOM: false } as const;

  for (let trim = 0; trim <= 3; trim += 1) {
    if (trim >= sample.length) break;
    try {
      new TextDecoder('utf-8', options).decode(trim === 0 ? sample : sample.subarray(0, sample.length - trim));
      return true;
    } catch {
      // Probably a split multi-byte character. Try one byte shorter.
    }
  }

  return false;
}

/**
 * Content types that are safe to render inline in a browser.
 *
 * `text/plain` is included because a browser renders it as text and cannot be
 * made to execute it. Everything else that reaches `/api/media` is served with
 * `Content-Disposition: attachment`, so a PDF cannot be used as a
 * script-hosting vector via a same-origin navigation.
 *
 * **`image/svg+xml` is explicitly excluded, and this is the one line in the
 * function that matters.** It starts with `image/`, so a naive
 * `startsWith('image/')` check would treat it as safe - and an SVG served inline
 * from this origin executes `<script>` in this origin, where it can reach the
 * session. `sniffContentType` already refuses to classify an SVG as an image, so
 * a value of `image/svg+xml` should never reach here; the check is present so that
 * a future code path which sets that content type explicitly still fails closed.
 */
export function isInlineSafe(contentType: string): boolean {
  if (contentType === 'text/plain') return true;
  if (contentType === 'image/svg+xml') return false;
  return contentType.startsWith('image/');
}

/**
 * Magic-byte prefix length exposed for the Worker, which reads only this many
 * bytes from the upload stream before deciding whether to buffer the rest.
 */
export const SNIFF_SAMPLE_BYTES = SAMPLE_BYTES;
